import { getProppingBounds } from '../products/models';
import tacHelpers from '../../state/tac/tacHelpers';
import productService from '../products';
import geometry from '../../scene/util/geometry';
import ItemContainer from '../../scene/ItemContainer';
import { ITEMS } from '../../constants';

export function calcAvailableSpaceInParent(clothesRailPos, closestItemPos) {
  return closestItemPos ? clothesRailPos - closestItemPos : clothesRailPos;
}

export function calcAvailableSpaceInScene(measuringAreaHeight, collidingRects) {
  if (collidingRects.length === 0) {
    // Floor is nearest colliding object
    return measuringAreaHeight;
  } else {
    const availableSpace = collidingRects.map(
      item => measuringAreaHeight - (item.y + item.height)
    );
    return Math.min(...availableSpace) || 0;
  }
}

function createMeasuringArea(sizingItem, globalCoords, parentItem) {
  // To find out if there are items under the clothes-rail we need
  // to create a rect that we use with getCollidingRects()
  const measuringArea = Object.assign({}, sizingItem, globalCoords);
  // The rect needs to stretch from the floor up to the clothes-rails y position
  measuringArea.height = measuringArea.y;
  measuringArea.y = 0;
  // We need to move the rects x position so that it won't
  // collide with the frames to the left
  measuringArea.x += parentItem.width - sizingItem.x + 1;
  // And then we need to make the width smaller so
  // that it won't collide with the frames on the right
  measuringArea.width -= (parentItem.width - sizingItem.x) * 2 + 1;
  return measuringArea;
}

export function getAvailableProppingBounds(
  proppingBounds,
  availableSpace,
  parentHeight
) {
  return proppingBounds
    .sort((a, b) => b.size.height - a.size.height)
    .filter(bound => {
      if (availableSpace) {
        if (bound.size.height < availableSpace) {
          return true;
        }
      } else {
        if (bound.size.height < parentHeight) {
          return true;
        }
      }
      return false;
    });
}

function getClosestItemBelow(items, omit = {}) {
  return items
    .filter(
      item =>
        (!item.itemid || item.itemid !== omit.itemid) &&
        item.y < omit.y &&
        !productService.isType(item, [ITEMS.FRAME, ITEMS.SHELVING_UNIT]) &&
        item.y > 0
    )
    .filter(item => {
      if (productService.isExtendable(omit)) {
        return productService.isExtendable(item);
      } else {
        return !productService.isExtendable(item);
      }
    })
    .sort((a, b) => b.y - a.y)
    .map(item => item.y + item.height)[0];
}

/**
 * Finds the largest (by height) propping that fits in the available space.
 * If there are several proppings of the highest fitting height, one of
 * those is chosen randomly. If none fits, null is returned.
 * @param {*} proppingBounds
 * @param {*} availableSpace
 * @param {*} padding
 * @returns The largest fitting propping, or null if none found.
 */
export function getLargestPropping(proppingBounds, availableSpace, padding) {
  const proppingsByHeight = {};
  proppingBounds.forEach(bound => {
    const height = bound.size.height;
    if (!proppingsByHeight[height]) proppingsByHeight[height] = [];
    proppingsByHeight[height].push(bound);
  });

  const availableHeightsDescending = Object.keys(proppingsByHeight).sort(
    (a, b) => b - a
  );

  let highestFittingHeight = null;
  for (let i = 0; i < availableHeightsDescending.length; i++) {
    if (availableHeightsDescending[i] < availableSpace - padding) {
      highestFittingHeight = availableHeightsDescending[i];
      break;
    }
  }

  if (!highestFittingHeight) return null;

  const proppingsOfHighestFittingHeight =
    proppingsByHeight[highestFittingHeight];
  const randomProppingOfHighestFittingHeight =
    proppingsOfHighestFittingHeight[
      Math.floor(Math.random() * proppingsOfHighestFittingHeight.length)
    ];

  return randomProppingOfHighestFittingHeight;
}

export function getRandomPropping(item) {
  //FIXME: This currently returns a number, while rest of pickPropping returns obj.
  const proppingBounds = getProppingBounds(item);
  return Math.floor(Math.random() * proppingBounds.length + 1);
}

export function replaceMovingItem(rects, movingItem) {
  /*
  If we are here due to another item moving in the scene,
  use that items moving rect instead of tac version.
  */
  const index = rects.findIndex(item => item.itemid === movingItem.itemid);
  if (index > -1) {
    rects[index] = movingItem;
  } else {
    rects.push(movingItem);
  }
}

export function pickMultiParentCrPropping(args) {
  const {
    tac,
    item,
    proppingItem,
    parentItem,
    parentContainer,
    grandParentContainer,
    movingItem,
  } = args;

  const proppingBounds = getProppingBounds(proppingItem || item);
  if (movingItem) {
    // Re-use existing propping if possible to speed things up
    const topAncestor = tacHelpers.getTopAncestor(tac, item);
    const connectsToItem =
      item.connectsTo && tacHelpers.getItem(tac, item.connectsTo.itemid);
    const connectsToTopAncestor =
      connectsToItem && tacHelpers.getTopAncestor(tac, connectsToItem);
    const movingItemTopAncestor =
      movingItem && tacHelpers.getTopAncestor(tac, movingItem);
    if (
      item.propping &&
      ((movingItemTopAncestor &&
        movingItemTopAncestor.itemid === topAncestor.itemid) ||
        (connectsToTopAncestor &&
          connectsToTopAncestor.itemid === movingItemTopAncestor.itemid))
    ) {
      return proppingBounds.find(bound => bound.id === item.propping);
    }
  }

  let largestFittingPropping;

  if (
    !parentItem ||
    !parentItem.itemid ||
    parentContainer instanceof ItemContainer
  ) {
    // Parent is Scene, return hanger bounds.
    return proppingBounds[0];
  } else if (parentItem.itemid && !productService.isExtendable(item)) {
    // We are snapped inside a frame/section
    const clothesRailPos = item.y;
    const closestItemPos = getClosestItemBelow(
      parentContainer
        ? parentContainer.children.map(child => child.item)
        : parentItem.items,
      item
    );
    const availableSpace = calcAvailableSpaceInParent(
      clothesRailPos,
      closestItemPos
    );
    const availableProppingBounds = getAvailableProppingBounds(
      proppingBounds,
      availableSpace,
      parentItem.height
    );

    largestFittingPropping = availableProppingBounds[0];
  } else if (parentItem.itemid && productService.isExtendable(item)) {
    // We are snapped on the outside of a frame/section
    let availableSpace;
    let closestItemPos;
    let clothesRailPos = item.y;

    if (parentContainer) {
      // First check if we have any items below us in this container
      closestItemPos = getClosestItemBelow(
        parentContainer.children.map(child => child.item),
        item
      );

      if (!closestItemPos && grandParentContainer) {
        /*
        If not, check if we have a grandParent and if so
        whether we have any items below us in its corresponding container
        */
        closestItemPos = getClosestItemBelow(
          grandParentContainer.children.map(child => child.item),
          { ...item, y: parentItem.y + item.y }
        );

        if (closestItemPos) {
          // We found something, adjust item values to be relative the grandParent instead
          clothesRailPos += parentItem.y;
        }
      }
    }

    if (closestItemPos) {
      availableSpace = calcAvailableSpaceInParent(
        clothesRailPos,
        closestItemPos
      );
    } else {
      // Still no collisions found, let's try once more against all other items in scene
      const tacParentItem = tacHelpers.getItem(tac, parentItem.itemid);
      const parentGlobalCoords = tacHelpers.getGlobalCoords(tacParentItem, tac);
      const itemGlobalCoords = {
        x: parentGlobalCoords.x + item.x,
        y: parentGlobalCoords.y + item.y,
        z: parentGlobalCoords.z + item.z,
      };

      const rects = tacHelpers.getRects(tac, item, tac);

      if (movingItem) {
        const movingFullSize = {
          ...movingItem,
          ...tacHelpers.getFullSize(movingItem),
        };

        replaceMovingItem(rects, movingFullSize);
      }

      const measuringArea = createMeasuringArea(
        item,
        itemGlobalCoords,
        parentItem
      );

      const collidingRects = geometry.getCollidingRects(measuringArea, rects);

      availableSpace = calcAvailableSpaceInScene(
        measuringArea.height,
        collidingRects
      );
    }

    largestFittingPropping = getLargestPropping(
      proppingBounds,
      availableSpace,
      20
    );
  }

  // Fallback to hangers
  return largestFittingPropping || proppingBounds[0];
}
